// src/pages/CustomerDetailsPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../scripts/api";
import {
  Pencil,
  X,
  Save,
  Search,
  RefreshCw,
  ClipboardList,
  SlidersHorizontal,
  Mail,
  Phone,
  BarChart3,
} from "lucide-react";

const HIDE_COLS = new Set(["id", "created_at", "updated_at"]);

const PREFERRED_ORDER = [
  "customer_num",
  "customer_name",
  "first_name",
  "last_name",
  "email",
  "bus_phone",
  "phone",
  "company",
  "address",
  "address2",
  "city",
  "prov",
  "province",
  "postal",
  "postal_code",
  "country",
  "cod",
  "salesman",
  "prim_cat",
  "sec_cat",
  "ter_cat",
  "notes",
  "map_path",
  "canoe",
  "added_by",
  "added_on",
  "modified_by",
  "modified_on",
];

function niceLabel(k) {
  const s = String(k || "").replace(/_/g, " ").trim();
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function isEmpty(v) {
  return v == null || String(v).trim() === "";
}

function fieldValue(row, k) {
  const v = row?.[k];
  return v == null ? "" : String(v);
}

function clampStr(v, n = 60) {
  const s = String(v || "");
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function AppModal({ onClose, children, width }) {
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className="modal"
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: width || "min(980px, 92vw)",
          maxHeight: "86vh",
          overflow: "hidden",
          padding: 0,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function SoftPill({ active, onClick, children, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        border: `1px solid var(--card-border)`,
        cursor: "pointer",
        borderRadius: 999,
        padding: "8px 10px",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        background: active ? "rgba(59,130,246,.16)" : "var(--card-bg)",
        color: "var(--fg)",
        boxShadow: active ? "0 0 0 3px rgba(59,130,246,.18)" : "none",
        transition: "transform 120ms ease, box-shadow 120ms ease, background 120ms ease",
        userSelect: "none",
        fontWeight: 800,
        fontSize: 12,
        lineHeight: 1,
      }}
      onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.99)")}
      onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
    >
      {children}
    </button>
  );
}

function ValueView({ value }) {
  return (
    <div
      style={{
        fontSize: 14,
        lineHeight: 1.35,
        color: isEmpty(value) ? "var(--muted)" : "var(--fg)",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {isEmpty(value) ? "—" : value}
    </div>
  );
}

function ValueEdit({ value, onChange, multiline }) {
  const common = { width: "100%", fontSize: 14, lineHeight: 1.35 };

  if (multiline) {
    return (
      <textarea
        className="input"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        style={{ ...common, resize: "vertical" }}
      />
    );
  }

  return (
    <input
      className="input"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      style={common}
    />
  );
}

function buildCustomerReport(rows) {
  const total = rows.length;
  const hasEmail = rows.filter((r) => String(r.email || "").trim()).length;
  const hasPhone = rows.filter((r) => String(r.bus_phone || r.phone || "").trim()).length;
  const hasAddress = rows.filter((r) => String(r.address || "").trim()).length;

  const missing = {
    email: total - hasEmail,
    phone: total - hasPhone,
    address: total - hasAddress,
    city: rows.filter((r) => !String(r.city || "").trim()).length,
    prov: rows.filter((r) => !String(r.prov || r.province || "").trim()).length,
    customer_num: rows.filter((r) => !String(r.customer_num || "").trim()).length,
    customer_name: rows.filter((r) => !String(r.customer_name || r.name || "").trim()).length,
  };

  const anomalies = {
    duplicateCustomerNum: (() => {
      const seen = new Map();
      const dups = new Set();
      for (const r of rows) {
        const n = String(r.customer_num || "").trim();
        if (!n) continue;
        if (seen.has(n)) dups.add(n);
        else seen.set(n, true);
      }
      return dups.size;
    })(),
    suspiciousEmails: rows.filter((r) => {
      const e = String(r.email || "").trim();
      if (!e) return false;
      return !e.includes("@") || e.includes(" ") || e.endsWith("@") || e.startsWith("@");
    }).length,
    suspiciousPhones: rows.filter((r) => {
      const p = String(r.bus_phone || r.phone || "").trim();
      if (!p) return false;
      const digits = p.replace(/\D/g, "");
      return digits.length > 0 && digits.length < 7;
    }).length,
  };

  const byProv = (() => {
    const m = new Map();
    for (const r of rows) {
      const p = String(r.prov || r.province || "").trim() || "(blank)";
      m.set(p, (m.get(p) || 0) + 1);
    }
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]).slice(0, 12);
  })();

  return {
    total,
    coverage: {
      emailPct: total ? Math.round((hasEmail / total) * 100) : 0,
      phonePct: total ? Math.round((hasPhone / total) * 100) : 0,
      addressPct: total ? Math.round((hasAddress / total) * 100) : 0,
    },
    missing,
    anomalies,
    byProv,
  };
}

function StatMiniSmall({ title, value, sub }) {
  return (
    <div className="scan-tile" style={{ textAlign: "left", padding: 10, minHeight: 0 }}>
      <div className="scan-title" style={{ fontSize: 11, opacity: 0.8, letterSpacing: 0.2 }}>
        {title}
      </div>
      <div style={{ fontSize: 16, fontWeight: 900, marginTop: 4, lineHeight: 1.1 }}>{value}</div>
      {sub ? (
        <div className="scan-sub" style={{ marginTop: 4, fontSize: 11, opacity: 0.75 }}>
          {sub}
        </div>
      ) : null}
    </div>
  );
}

function CustomerCard({ customer, onOpen }) {
  const name = customer.customer_name || customer.name || "(no name)";
  const num = customer.customer_num ? `#${customer.customer_num}` : "";
  const place = [customer.city || "", customer.prov || customer.province || ""].filter(Boolean).join(", ");

  const email = customer.email ? String(customer.email) : "";
  const phone = customer.bus_phone || customer.phone || "";

  const secondaryParts = [];
  if (place) secondaryParts.push(place);
  const contact = [email, phone].filter(Boolean).join(" • ");
  if (contact) secondaryParts.push(contact);
  const secondary = secondaryParts.join(" • ") || "—";

  return (
    <button
      type="button"
      onClick={onOpen}
      className="card"
      style={{
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        transition: "transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease",
        color: "var(--fg)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(59,130,246,.35)";
        e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.18)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--card-border)";
        e.currentTarget.style.boxShadow = "none";
      }}
      onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.992)")}
      onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
      title={name}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
        <div style={{ fontWeight: 900, fontSize: 14, lineHeight: 1.25, color: "var(--fg)" }}>
          {clampStr(name, 64)}
        </div>

        {num ? <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 800 }}>{num}</div> : null}
      </div>

      <div style={{ marginTop: 8, fontSize: 12, lineHeight: 1.35, color: "var(--muted)" }}>{secondary}</div>
    </button>
  );
}

export default function CustomerDetailsPage() {
  const [schemaCols, setSchemaCols] = useState([]);
  const [rows, setRows] = useState([]);

  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const [filterProv, setFilterProv] = useState("");
  const [filterHasEmail, setFilterHasEmail] = useState(false);
  const [filterHasPhone, setFilterHasPhone] = useState(false);
  const [sortKey, setSortKey] = useState("name");

  const [openId, setOpenId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});

  const [auditOpen, setAuditOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [custReport, setCustReport] = useState(null);
  const [custReportLoading, setCustReportLoading] = useState(false);

  const debounceRef = useRef(null);

  const loadSchema = async () => {
    const { data } = await api.get("/api/customers/schema");
    const cols = Array.isArray(data?.columns) ? data.columns : [];
    setSchemaCols(cols);
  };

  const loadList = async (query) => {
    setLoading(true);
    setMsg("");
    try {
      const { data } = await api.get("/api/customers", { params: { q: query || "", limit: 5000 } });
      const r = Array.isArray(data?.rows) ? data.rows : [];
      setRows(r);
    } catch (e) {
      setMsg(e?.response?.data?.error || e?.response?.data?.message || "Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await loadSchema();
        await loadList("");
      } catch (e) {
        setMsg(e?.response?.data?.error || e?.message || "Failed to init");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => loadList(q), 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const provOptions = useMemo(() => {
    const s = new Set();
    for (const r of rows) {
      const p = String(r.prov || r.province || "").trim();
      if (p) s.add(p);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const s = String(q || "").trim().toLowerCase();
    let out = rows;

    if (s) {
      out = out.filter((r) => {
        const name = String(r.customer_name || r.name || "").toLowerCase();
        const num = String(r.customer_num || "").toLowerCase();
        return name.includes(s) || num.includes(s);
      });
    }

    if (filterProv) out = out.filter((r) => String(r.prov || r.province || "").trim() === filterProv);
    if (filterHasEmail) out = out.filter((r) => !isEmpty(r.email));
    if (filterHasPhone) out = out.filter((r) => !isEmpty(r.bus_phone) || !isEmpty(r.phone));

    const safe = (v) => String(v || "").trim().toLowerCase();
    const safeNum = (v) => {
      const n = String(v || "").trim();
      const x = Number(n);
      return Number.isFinite(x) ? x : n;
    };

    out = [...out].sort((a, b) => {
      if (sortKey === "num") {
        const an = safeNum(a.customer_num);
        const bn = safeNum(b.customer_num);
        if (typeof an === "number" && typeof bn === "number") return an - bn;
        return String(an).localeCompare(String(bn));
      }
      if (sortKey === "city") {
        return safe(a.city).localeCompare(safe(b.city)) || safe(a.customer_name).localeCompare(safe(b.customer_name));
      }
      return safe(a.customer_name || a.name).localeCompare(safe(b.customer_name || b.name));
    });

    return out;
  }, [rows, q, filterProv, filterHasEmail, filterHasPhone, sortKey]);

  // ✅ Hooks belong here (top-level in component), not inside callbacks
  const customerStats = useMemo(() => buildCustomerReport(filteredRows), [filteredRows]);

  const selected = useMemo(() => rows.find((r) => r.id === openId) || null, [rows, openId]);

  const renderFields = useMemo(() => {
    const all = schemaCols.map((c) => c.name).filter((n) => n && !HIDE_COLS.has(n));
    const set = new Set(all);

    const ordered = [];
    for (const k of PREFERRED_ORDER) if (set.has(k)) ordered.push(k);
    for (const k of all) if (!ordered.includes(k)) ordered.push(k);

    return ordered;
  }, [schemaCols]);

  const openCustomer = (r) => {
    setOpenId(r.id);
    setEditing(false);
    setDraft({});
    setAuditOpen(false);
  };

  const closeModal = () => {
    setOpenId(null);
    setEditing(false);
    setDraft({});
    setAuditOpen(false);
  };

  const startEdit = () => {
    if (!selected) return;
    setDraft({ ...selected });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft({});
  };

  const saveEdit = async () => {
    if (!selected) return;
    setLoading(true);
    setMsg("");
    try {
      const payload = { ...draft };
      delete payload.id;

      const { data } = await api.put(`/api/customers/${selected.id}`, payload);
      setRows((prev) => prev.map((x) => (x.id === selected.id ? data : x)));

      setEditing(false);
      setDraft({});
      setMsg("Saved.");
    } catch (e) {
      setMsg(e?.response?.data?.error || e?.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const title = selected?.customer_name || selected?.name || "Customer";
  const subtitle = selected
    ? `${selected.customer_num ? `#${selected.customer_num}` : "—"}${selected.email ? ` • ${selected.email}` : ""}`
    : "";

  const activeCount = filteredRows.length;

  return (
    <div className="container" style={{ paddingTop: 18, maxWidth: 1100 }}>
      <div className="card" style={{ padding: 16, borderRadius: 18 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontWeight: 950, fontSize: 18, color: "var(--fg)" }}>Customers</div>
            <div style={{ fontSize: 12, marginTop: 4, color: "var(--muted)" }}>
              {activeCount} shown • {rows.length} loaded
            </div>
          </div>

          <button
            className="btn-outline"
            onClick={() => {
              setCustReport(buildCustomerReport(rows));
              setReportsOpen(true);
            }}
            disabled={loading}
            title="Customer reports"
            type="button"
          >
            <BarChart3 size={16} />
          </button>

          <button className="btn-outline" onClick={() => loadList(q)} disabled={loading} title="Refresh" type="button">
            <RefreshCw size={16} />
          </button>

          <div
            className="card"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              borderRadius: 14,
              minWidth: 320,
              flex: "0 1 460px",
            }}
          >
            <Search size={16} style={{ opacity: 0.8 }} />
            <input
              className="input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search customer name or customer #…"
              style={{
                border: "none",
                padding: 0,
                boxShadow: "none",
                background: "transparent",
              }}
            />
          </div>
        </div>

        <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "var(--muted)" }}>
            <SlidersHorizontal size={16} />
            <span style={{ fontWeight: 800, fontSize: 12 }}>Filters</span>
          </div>

          <select
            className="input"
            value={filterProv}
            onChange={(e) => setFilterProv(e.target.value)}
            style={{ width: 140 }}
            title="Province"
          >
            <option value="">All prov</option>
            {provOptions.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>

          <SoftPill active={filterHasEmail} onClick={() => setFilterHasEmail((v) => !v)} title="Only customers with email">
            <Mail size={14} />
            Email
          </SoftPill>

          <SoftPill active={filterHasPhone} onClick={() => setFilterHasPhone((v) => !v)} title="Only customers with phone">
            <Phone size={14} />
            Phone
          </SoftPill>

          <div style={{ flex: 1 }} />

          <select className="input" value={sortKey} onChange={(e) => setSortKey(e.target.value)} style={{ width: 160 }} title="Sort">
            <option value="name">Sort: Name</option>
            <option value="num">Sort: Customer #</option>
            <option value="city">Sort: City</option>
          </select>
        </div>

        {msg ? (
          <div style={{ marginTop: 10, fontSize: 12, color: "var(--muted)" }}>
            {msg}
          </div>
        ) : null}

        {/* ✅ stats for the CURRENT filtered view */}
        <div
          className="scan-grid"
          style={{
            marginTop: 12,
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 10,
          }}
        >
          <StatMiniSmall title="Customers" value={String(customerStats.total)} sub="in view" />
          <StatMiniSmall
            title="Email coverage"
            value={`${customerStats.coverage.emailPct}%`}
            sub={`${customerStats.missing.email} missing`}
          />
          <StatMiniSmall
            title="Phone coverage"
            value={`${customerStats.coverage.phonePct}%`}
            sub={`${customerStats.missing.phone} missing`}
          />
          <StatMiniSmall
            title="Address coverage"
            value={`${customerStats.coverage.addressPct}%`}
            sub={`${customerStats.missing.address} missing`}
          />
        </div>

        <div
          style={{
            marginTop: 14,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 12,
          }}
        >
          {filteredRows.map((r) => (
            <CustomerCard key={r.id} customer={r} onOpen={() => openCustomer(r)} />
          ))}

          {filteredRows.length === 0 ? (
            <div style={{ padding: 12, color: "var(--muted)" }}>No matching customers.</div>
          ) : null}
        </div>
      </div>

      {openId && selected ? (
        <AppModal onClose={closeModal} width="min(980px, 92vw)">
          <div
            style={{
              padding: 14,
              borderBottom: "1px solid var(--card-border)",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 950,
                  fontSize: 18,
                  lineHeight: 1.2,
                  color: "var(--fg)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {title}
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted)" }}>{subtitle}</div>
            </div>

            <button
              className="btn-outline"
              onClick={() => setAuditOpen(true)}
              disabled={loading}
              title="Audit"
              type="button"
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <ClipboardList size={16} />
              Audit
            </button>

            {!editing ? (
              <button className="btn-outline" onClick={startEdit} disabled={loading} title="Edit" type="button">
                <Pencil size={16} />
              </button>
            ) : (
              <button className="btn" onClick={saveEdit} disabled={loading} title="Save" type="button">
                <Save size={16} style={{ marginRight: 8 }} />
                Save
              </button>
            )}

            {editing ? (
              <button className="btn-outline" onClick={cancelEdit} disabled={loading} title="Cancel" type="button">
                <X size={16} />
              </button>
            ) : (
              <button className="btn-outline" onClick={closeModal} title="Close" type="button">
                <X size={16} />
              </button>
            )}
          </div>

          <div style={{ padding: 14, overflowY: "auto", maxHeight: "calc(86vh - 62px)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
              {renderFields.map((k) => {
                const v = editing ? fieldValue(draft, k) : fieldValue(selected, k);
                const multiline = k === "notes" || k === "address" || k === "address2" || k === "map_path";

                return (
                  <div key={k} className="card" style={{ padding: 12, borderRadius: 14 }}>
                    <div style={{ fontSize: 11, letterSpacing: 0.2, color: "var(--muted)", fontWeight: 800 }}>
                      {niceLabel(k)}
                    </div>

                    <div style={{ marginTop: 8 }}>
                      {!editing ? (
                        <ValueView value={v} />
                      ) : (
                        <ValueEdit
                          value={v}
                          multiline={multiline}
                          onChange={(nv) => setDraft((d) => ({ ...d, [k]: nv }))}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 12, fontSize: 12, color: "var(--muted)" }}>
              Schema-driven view: new CSV columns appear automatically.
            </div>
          </div>

          {reportsOpen ? (
            <AppModal onClose={() => setReportsOpen(false)} width="min(920px, 92vw)">
              <div
                style={{
                  padding: 14,
                  borderBottom: "1px solid var(--card-border)",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 950, fontSize: 18, color: "var(--fg)" }}>Customer Reports</div>
                  <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted)" }}>
                    Stats, anomalies, missing data (based on loaded customers)
                  </div>
                </div>

                <button className="btn-outline" onClick={() => setReportsOpen(false)} title="Close" type="button">
                  <X size={16} />
                </button>
              </div>

              <div style={{ padding: 14, overflowY: "auto", maxHeight: "calc(86vh - 62px)" }}>
                {!custReport ? (
                  <div className="muted">No report.</div>
                ) : (
                  <>
                    <div
                      className="scan-grid"
                      style={{
                        marginTop: 0,
                        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                        gap: 10,
                      }}
                    >
                      <StatMiniSmall title="Total customers" value={String(custReport.total)} sub="loaded" />
                      <StatMiniSmall
                        title="Missing email"
                        value={String(custReport.missing.email)}
                        sub={`${custReport.coverage.emailPct}% coverage`}
                      />
                      <StatMiniSmall
                        title="Missing phone"
                        value={String(custReport.missing.phone)}
                        sub={`${custReport.coverage.phonePct}% coverage`}
                      />
                      <StatMiniSmall
                        title="Missing address"
                        value={String(custReport.missing.address)}
                        sub={`${custReport.coverage.addressPct}% coverage`}
                      />
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12, marginTop: 12 }}>
                      <div className="card" style={{ padding: 12 }}>
                        <div style={{ fontWeight: 900, color: "var(--fg)" }}>Anomalies</div>
                        <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                          <div style={{ color: "var(--fg)" }}>
                            <b>Duplicate customer #</b>: {custReport.anomalies.duplicateCustomerNum}
                          </div>
                          <div style={{ color: "var(--fg)" }}>
                            <b>Suspicious emails</b>: {custReport.anomalies.suspiciousEmails}
                          </div>
                          <div style={{ color: "var(--fg)" }}>
                            <b>Suspicious phones</b>: {custReport.anomalies.suspiciousPhones}
                          </div>
                        </div>
                        <div style={{ marginTop: 10, fontSize: 12, color: "var(--muted)" }}>
                          “Suspicious” = quick heuristics (not gospel).
                        </div>
                      </div>

                      <div className="card" style={{ padding: 12 }}>
                        <div style={{ fontWeight: 900, color: "var(--fg)" }}>Missing fields</div>
                        <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                          <div style={{ color: "var(--fg)" }}>
                            <b>Missing name</b>: {custReport.missing.customer_name}
                          </div>
                          <div style={{ color: "var(--fg)" }}>
                            <b>Missing customer #</b>: {custReport.missing.customer_num}
                          </div>
                          <div style={{ color: "var(--fg)" }}>
                            <b>Missing city</b>: {custReport.missing.city}
                          </div>
                          <div style={{ color: "var(--fg)" }}>
                            <b>Missing prov</b>: {custReport.missing.prov}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card" style={{ padding: 12, marginTop: 12 }}>
                      <div style={{ fontWeight: 900, color: "var(--fg)" }}>Top provinces</div>
                      <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}>
                        {custReport.byProv.map(([prov, count]) => (
                          <div key={prov} className="card" style={{ padding: 10 }}>
                            <div style={{ fontWeight: 900, color: "var(--fg)" }}>{prov}</div>
                            <div style={{ marginTop: 4, color: "var(--muted)", fontSize: 12 }}>{count} customers</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </AppModal>
          ) : null}

          {auditOpen ? (
            <AppModal onClose={() => setAuditOpen(false)} width="min(520px, 92vw)">
              <div style={{ padding: 16 }}>
                <div className="row" style={{ justifyContent: "space-between" }}>
                  <div style={{ fontWeight: 900, fontSize: 16, color: "var(--fg)" }}>Customer Audit</div>
                  <button className="btn-outline" onClick={() => setAuditOpen(false)} title="Close" type="button">
                    <X size={16} />
                  </button>
                </div>

                <div style={{ marginTop: 10, fontSize: 12, color: "var(--muted)" }}>
                  Drop links/tools here (duplicate detection, missing fields report, geo clustering, spend analysis, etc).
                </div>

                <div className="card" style={{ marginTop: 12, borderStyle: "dashed" }}>
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>(empty)</div>
                </div>
              </div>
            </AppModal>
          ) : null}
        </AppModal>
      ) : null}
    </div>
  );
}
